import { useMemo, useState, type ReactElement } from 'react'
import { isEmpty, searchIncludes, type CompanyId } from '@terros/common'
import { Text, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import { useRoles, useTranslation, type DashboardAudienceTarget } from '@front/model'
import { AudienceOptionList, AudienceOptionRow } from './AudienceOptionList'

type Props = {
  companyId: CompanyId | undefined
  selectedTarget: DashboardAudienceTarget | undefined
  onSelect: (target: DashboardAudienceTarget) => void
}

export function AudienceRoleOptions({ companyId, selectedTarget, onSelect }: Props): ReactElement {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { data: roles = [], isLoading, error } = useRoles({ companyId, showArchived: false, enabled: true })
  useToastError(error)
  const filteredRoles = useMemo(() => {
    const query = search.trim()
    if (isEmpty(query)) return roles
    return roles.filter((role) => searchIncludes([role.name], query))
  }, [roles, search])

  return (
    <>
      <TextInput
        placeholder={t('Search roles')}
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />
      <AudienceOptionList>
        {filteredRoles.map((role) => (
          <AudienceOptionRow
            isSelected={selectedTarget?.entityId === role.roleId}
            key={role.roleId}
            onSelect={() => onSelect({ entityId: role.roleId, name: role.name })}
          >
            {role.name}
          </AudienceOptionRow>
        ))}
        {isLoading && <Text c='dimmed'>{t('Loading...')}</Text>}
        {!isLoading && isEmpty(filteredRoles) && <Text c='dimmed'>{t('No roles found')}</Text>}
      </AudienceOptionList>
    </>
  )
}
